import React from "react";
import {
    useUpdatePlaydateInviteMutation,
    PlaydateInvite,
    PlaydateGuestType,
    UpdatePlaydateInviteInput,
} from "../../../gql/types";
import { DraftPlaydateInvite } from "../types";
import { convertSelectedDayPartsToScheduleOptionInputs } from "../../../service/playdate/converters/converter";
import { showToast } from "../../../toast/toast";
import PlaydateInviteCreationView from "../view/PlaydateInviteCreationView";
import PortalRoot from "../../portal/PortalRoot";
import { OverlayOpacity } from "../../portal/types";
import { useLayout } from "../../layout/LayoutContext";
import { appEvents, AppEventType } from "../../../events/app";
import { useToastContext } from "contexts/ToastContext";

interface PlaydateInviteEditWorkflowProps {
    invite: PlaydateInvite;
    onBack: () => void;
    onShare?: (updatedInvite: PlaydateInvite) => void;
    baseZIndex?: number;
    onDataChange: (success: boolean, invite?: PlaydateInvite) => void;
}

const PlaydateInviteEditWorkflow: React.FC<PlaydateInviteEditWorkflowProps> = ({
    invite,
    onBack,
    baseZIndex,
    onDataChange,
}) => {
    const [updateInvite] = useUpdatePlaydateInviteMutation();
    const { baseZIndex: layoutZIndex } = useLayout();
    const { pushToastEvent } = useToastContext();

    if (!baseZIndex) {
        baseZIndex = layoutZIndex;
    }

    const handleUpdate = async (draft: DraftPlaydateInvite, setLoading: (loading: boolean) => void) => {
        try {
            setLoading(true);
            const input: UpdatePlaydateInviteInput = {
                title: draft.title,
                location: draft.location,
                playdateType: draft.playdateType,
                optionType: draft.optionType,
                scheduleOptions: convertSelectedDayPartsToScheduleOptionInputs(draft.scheduleOptions),
                rsvpDeadline: draft.rsvpDeadline,
                details: draft.details,
                guestIDs: draft.guestConnections.map((c) => c.id),
                externalGuests: draft.externalGuests,
            };

            const { data } = await updateInvite({
                variables: {
                    inviteID: invite.inviteID,
                    input,
                },
            });

            if (!data?.updatePlaydateInvite) {
                throw new Error("No updated invite returned.");
            }

            pushToastEvent({ eventType: "success", message: "Updated the invite successfully!" });
            console.log("pushing toast");
            onDataChange(true, data.updatePlaydateInvite);
        } catch (error) {
            appEvents.emit(AppEventType.RESET_ACTION_BUTTON);
            showToast("error", "Failed to update invite. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <PortalRoot
            baseZIndex={baseZIndex}
            overlayOpacity={OverlayOpacity.COVERED}
        >
            <PlaydateInviteCreationView
                mode={"edit"}
                onBack={onBack}
                onContinue={handleUpdate}
                baseZIndex={baseZIndex}
                invite={invite}
            />
        </PortalRoot>
    );
};

export default PlaydateInviteEditWorkflow;
